import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { Language, translationKeys, TranslationEntry } from '@raptor-fe/core';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { ServiceUtils } from '../service-utils/service-utils';

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    private readonly applicationContext = inject(ApplicationContextStore);
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    private shouldIncludeOriginalLabel = this.applicationContext.shouldIncludeOriginalLabelInTranslation();

    /**
     * Makes a request to get the translation dictionary for the provided language, saves the translations to storage, and sets the current language in storage.
     * Also triggers a reload of the dictionary in the application context so that translations are updated across the app.
     * @param language The language for which to retrieve the dictionary. If not provided, the selected language from the application context or the default language will be used.
     * @returns An observable of the translation entries.
     */
    getDictionary(language: Language | null = null): Observable<TranslationEntry[]> {
        language = language ?? this.applicationContext.selectedLanguage();

        if (!language?.id) {
            this.applicationContext.setDictionary(translationKeys);
            return of(translationKeys);
        }

        return this.http.post<TranslationEntry[]>(`${this.serviceUtils.getBaseUrl('language')}/dictionary/${language.id}`, translationKeys).pipe(
            tap((data) => {
                this.applicationContext.setCurrentLanguage(language);
                this.applicationContext.setDictionary(data);
            }),
            catchError((error) => {
                this.applicationContext.setDictionary(translationKeys);
                console.error(`Error fetching translation dictionary for language ${language?.id}:`, error);
                return throwError(() => error);
            }),
        );
    }

    /**
     * Re-fetches the dictionary for the already-selected language so wording edits made since the
     * last sign-in reach a returning session. Unlike {@link getDictionary}, a failure keeps the cached
     * dictionary rather than resetting it to raw labels, because a transient error at app start must
     * not strip the tenant's wording from a session that already had it.
     * @returns An observable of the fetched entries, or null when nothing was fetched.
     */
    refreshDictionary(): Observable<TranslationEntry[] | null> {
        const language = this.applicationContext.selectedLanguage();

        if (!language?.id) {
            return of(null);
        }

        return this.http.post<TranslationEntry[]>(`${this.serviceUtils.getBaseUrl('language')}/dictionary/${language.id}`, translationKeys).pipe(
            tap((data) => this.applicationContext.setDictionary(data)),
            catchError((error) => {
                console.error(`Error refreshing translation dictionary for language ${language.id}; keeping the cached dictionary.`, error);
                return of(null);
            }),
        );
    }

    /**
     * Makes a request to get all languages associated with the current blue instance.
     * @returns An observable of the available languages.
     */
    getAvailableLanguages(): Observable<Language[]> {
        return this.http.get<Language[]>(this.serviceUtils.getBaseUrl('language'));
    }

    /**
     * Retrieves the translation for a specific feature, component, and label.
     * @param feature The feature name.
     * @param component The component name.
     * @param label The label name.
     * @returns The translated string if found, otherwise the original label.
     */
    getTranslation(feature: string, component: string, label: string): string {
        const dictEntry = this.applicationContext.dictionary()?.get(`${feature}|${component}|${label}`);

        if (!dictEntry) {
            return label;
        }
        if (dictEntry === label || !this.shouldIncludeOriginalLabel) {
            return dictEntry;
        }
        return `${dictEntry}/${label}`;
    }
}
